import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { CloudDownloadOutlined, FileOutlined } from '@ant-design/icons';
import { getFileIconSrc, getFileTypeLabel } from '@utils/fileIcon.util';
import { resolveChatMediaUrl } from '@utils/chatMediaUrl.util';
import { getAttachmentPreviewImageRaw } from '@utils/chatAttachmentPreview.util';
import { ChatAttachmentPreview } from './ChatAttachmentPreview';

const CardRoot = styled.div`
    width: 100%;
    max-width: 100%;
    min-width: 0;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)')};
    background: ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.92)')};
    box-shadow: ${({ $dark }) => ($dark ? '0 14px 34px rgba(0,0,0,0.28)' : '0 14px 34px rgba(2,6,23,0.10)')};
`;

const PreviewArea = styled.div`
    height: 200px;
    width: 100%;
    overflow: hidden;
    background: ${({ $dark }) => ($dark ? 'rgba(0,0,0,0.18)' : 'rgba(2,6,23,0.03)')};
    border-top: 1px solid ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)')};
`;

const Header = styled.div`
    padding: 10px 12px;
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 72px;
    min-width: 0;
`;

const IconBox = styled.div`
    width: 42px;
    height: 42px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
    background: ${({ $dark }) => ($dark ? 'rgba(15,23,42,0.55)' : 'rgba(2,6,23,0.04)')};
    border: 1px solid ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.08)')};
`;

const Name = styled.div`
    font-size: 14px;
    font-weight: 800;
    color: ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.92)' : '#0f172a')};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Meta = styled.div`
    font-size: 12px;
    opacity: ${({ $dark }) => ($dark ? 0.68 : 0.72)};
    color: ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.82)' : 'rgba(0,0,0,0.78)')};
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const Subtle = styled.div`
    font-size: 12px;
    opacity: ${({ $dark }) => ($dark ? 0.55 : 0.62)};
    margin-top: 2px;
`;

const HoverBtn = styled.button`
    width: 36px;
    height: 36px;
    border-radius: 10px;
    border: 1px solid ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.10)')};
    background: ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.06)' : 'rgba(2,6,23,0.04)')};
    color: ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.88)' : 'rgba(0,0,0,0.75)')};
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

function formatFileSize(bytes) {
    const b = Number(bytes);
    if (!Number.isFinite(b) || b <= 0) return '';
    const units = ['B', 'KB', 'MB', 'GB'];
    let v = b;
    let i = 0;
    while (v >= 1024 && i < units.length - 1) {
        v /= 1024;
        i += 1;
    }
    const fixed = i === 0 ? String(Math.round(v)) : v >= 10 ? v.toFixed(0) : v.toFixed(1);
    return `${fixed} ${units[i]}`;
}

export const ChatAttachmentCard = React.memo(function ChatAttachmentCard({
    attachment,
    attachmentIndex,
    messagePreviewImages,
    dark,
    onOpen,
    onDownload,
}) {
    const [hover, setHover] = useState(false);
    const rawUrl = attachment?.url ?? attachment?.fileUrl ?? attachment?.path ?? attachment?.href ?? '';
    const url = resolveChatMediaUrl(rawUrl) ?? '';
    const name = attachment?.title ?? attachment?.name ?? attachment?.filename ?? 'Attachment';
    const mimeType = (attachment?.mimeType ?? attachment?.type ?? '').toLowerCase();
    const rawPreviewImg = getAttachmentPreviewImageRaw(attachment, attachmentIndex, messagePreviewImages);
    const rawThumb =
        rawPreviewImg ??
        attachment?.previewThumbnailUrl ??
        attachment?.thumbnailUrl ??
        attachment?.previewUrl ??
        null;
    const previewThumbnailUrl = rawThumb ? resolveChatMediaUrl(rawThumb) : null;
    const sizeLabel = useMemo(() => formatFileSize(attachment?.size), [attachment?.size]);
    const typeLabel = useMemo(() => getFileTypeLabel({ name, url }), [name, url]);
    const iconSrc = useMemo(() => getFileIconSrc({ name, url, mimeType }), [name, url, mimeType]);

    return (
        <CardRoot
            $dark={dark}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onOpen?.(attachment)}
            onClick={() => onOpen?.(attachment)}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{ cursor: onOpen ? 'pointer' : 'default' }}
        >
            <Header>
                <IconBox $dark={dark}>
                    {iconSrc ? (
                        <img src={iconSrc} alt="" style={{ width: 28, height: 28, objectFit: 'contain' }} />
                    ) : (
                        <FileOutlined style={{ fontSize: 22, opacity: 0.7 }} />
                    )}
                </IconBox>

                <div style={{ minWidth: 0, flex: 1 }}>
                    <Name $dark={dark} title={name}>
                        {name}
                    </Name>
                    <Meta $dark={dark}>
                        <span>{typeLabel}</span>
                        {sizeLabel ? <span style={{ opacity: 0.9 }}>• {sizeLabel}</span> : null}
                    </Meta>
                    <Subtle $dark={dark}>Tap to preview · use the download button to save</Subtle>
                </div>

                {onDownload ? (
                    <div
                        style={{
                            width: 36,
                            height: 36,
                            flexShrink: 0,
                            opacity: 1,
                        }}
                    >
                        <HoverBtn
                            type="button"
                            $dark={dark}
                            aria-label="Download"
                            title="Download"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDownload?.(attachment);
                            }}
                        >
                            <CloudDownloadOutlined />
                        </HoverBtn>
                    </div>
                ) : null}
            </Header>

            <PreviewArea $dark={dark}>
                <ChatAttachmentPreview
                    url={url}
                    name={name}
                    mimeType={mimeType}
                    previewThumbnailUrl={previewThumbnailUrl}
                    dark={dark}
                />
            </PreviewArea>
        </CardRoot>
    );
});

